/**
 * Order request building: the ONE place that decides what the booking panel
 * sends to pricing (customerPriceCheck) and to orders (createOrder).
 *
 * Why it matters. Anything inside a quote's locations[] is copied into the
 * order's paths, which every matched driver receives before accepting, which
 * the public /trip page shows, and which pricing keeps in a public quote for
 * an hour. Receiver names, phones and instructions must therefore never ride
 * along with a stop to pricing. They are captured after the vehicle is
 * chosen and go to createOrder as a separate stops[] list, merged by index.
 *
 * Pure functions only (no Vue, no axios), so they can be checked in
 * isolation.
 */
import type { OrderType } from "./orderFeatures";
import { type PickupMode, resolveScheduledTime } from "./orderTime";

/** Receiver details for one stop. Every field is optional. */
export interface StopDetails {
  contact_name: string;
  /** E.164 from the phone input (+2547...), or "" */
  contact_phone: string;
  /** Invoice, delivery note or PO number */
  reference: string;
  instructions: string;
  /** Stored only. SMS is not live, so the panel never sets it (no UI). */
  notify_sms: boolean;
}

/** A stop as pricing receives it: today's exact shape. */
export interface QuoteLocation {
  name: string;
  formatted_address?: string;
  lat: number;
  lng: number;
  place_id: string;
  key: number;
}

/** A stop in the booking panel: the quote fields plus local-only extras. */
export interface DraftLocation extends QuoteLocation {
  /** Stable id for v-for and for merging details back; never sent. */
  uid: string;
  /** Receiver details; sent to createOrder only, never to pricing. */
  details: StopDetails;
}

export const STOP_LIMITS = {
  contact_name: 80,
  reference: 40,
  instructions: 500,
} as const;

export const GOODS_LIMITS = { min: 2, max: 120 } as const;

export function emptyStopDetails(): StopDetails {
  return { contact_name: "", contact_phone: "", reference: "", instructions: "", notify_sms: false };
}

export function newStopUid(): string {
  try {
    if (typeof crypto !== "undefined" && typeof crypto.randomUUID === "function") return crypto.randomUUID();
  } catch {
    // Not a secure context: fall through.
  }
  return `s-${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 10)}`;
}

export function newDraftLocation(key: number = Date.now()): DraftLocation {
  return {
    name: "",
    formatted_address: "",
    key,
    lat: 0,
    lng: 0,
    place_id: "",
    uid: newStopUid(),
    details: emptyStopDetails(),
  };
}

/** Trim every text field; keep notify_sms only when there is a phone. */
export function cleanStopDetails(d: Partial<StopDetails> | null | undefined): StopDetails {
  const s = (v: unknown) => (typeof v === "string" ? v.trim() : "");
  const contact_phone = s(d?.contact_phone);
  return {
    contact_name: s(d?.contact_name),
    contact_phone,
    reference: s(d?.reference),
    instructions: s(d?.instructions),
    notify_sms: !!d?.notify_sms && contact_phone !== "",
  };
}

export function hasStopDetails(d: Partial<StopDetails> | null | undefined): boolean {
  const c = cleanStopDetails(d);
  return !!(c.contact_name || c.contact_phone || c.reference || c.instructions);
}

/**
 * The customerPriceCheck locations: exactly the six fields pricing has always
 * received. Built from an allow-list rather than by deleting known extras, so
 * a field added to the draft later cannot leak to pricing by default.
 */
export function toQuoteLocations(locations: ReadonlyArray<QuoteLocation>): QuoteLocation[] {
  return locations.map((l) => ({
    name: l.name,
    formatted_address: l.formatted_address ?? "",
    key: l.key,
    lat: l.lat,
    lng: l.lng,
    place_id: l.place_id,
  }));
}

// ─── Goods description ──────────────────────────────────────────────────

// A Kenyan mobile number in free text: orders' containsKePhone
// (services/orders helpers/phone.js), which rejects createOrder with
// goods_description_has_phone. The server's source is:
//
//   const joined = text.replace(/(?<=\d)[\s\-.()]+(?=[\d+])/g, '')
//                      .replace(/(?<=\+)[\s]+/g, '');
//   return /(?<!\d)(?:\+?254|0)[17]\d{8}(?!\d)/.test(joined);
//
// It joins digit groups people split with separators ("0712 345 678"),
// then looks for the number with digit boundaries, so a longer digit run
// ("SN 20712345678") is not a phone. The same steps are written here
// without lookbehind, which Safari before 16.4 cannot parse (a parse error
// would break the whole bundle): (?<=\d)X becomes (\d)X → "$1", and
// (?<!\d) becomes (?:^|\D). Keep the two in step.
const KE_PHONE_JOIN_DIGITS = /(\d)[\s\-.()]+(?=[\d+])/g;
const KE_PHONE_JOIN_PLUS = /\+\s+/g;
const KE_PHONE_RE = /(?:^|\D)(?:\+?254|0)[17]\d{8}(?!\d)/;

/** Trimmed, with control characters (newlines included) turned into spaces. */
export function cleanGoodsDescription(text: unknown): string {
  if (typeof text !== "string") return "";
  // eslint-disable-next-line no-control-regex
  return text.replace(/[\u0000-\u001f\u007f]+/g, " ").trim();
}

export function goodsHasPhone(text: string): boolean {
  if (typeof text !== "string") return false;
  const joined = text.replace(KE_PHONE_JOIN_DIGITS, "$1").replace(KE_PHONE_JOIN_PLUS, "+");
  return KE_PHONE_RE.test(joined);
}

/** The inline message for "What are you sending?", or "" when it is fine. */
export function goodsDescriptionError(text: unknown): string {
  const t = cleanGoodsDescription(text);
  if (!t) return "Add what you're sending";
  if (t.length < GOODS_LIMITS.min) return `Use at least ${GOODS_LIMITS.min} characters`;
  if (t.length > GOODS_LIMITS.max) return `Use ${GOODS_LIMITS.max} characters or fewer`;
  if (goodsHasPhone(t)) return "Drivers see this before they accept, so put phone numbers in the stop contact.";
  return "";
}

// ─── createOrder ────────────────────────────────────────────────────────

/** One createOrder stops[] entry (orders merges it onto the quoted stop by index). */
export interface CreateOrderStop {
  index: number;
  contact_name: string;
  contact_phone: string;
  reference: string;
  instructions: string;
  notify_sms: boolean;
  /** Echo of the quoted stop; orders answers stops_mismatch if it moved. */
  lat: number;
  lng: number;
}

/** Only stops with something filled in; index 0 is the pickup. */
export function buildStopsPayload(locations: ReadonlyArray<DraftLocation>): CreateOrderStop[] {
  const out: CreateOrderStop[] = [];
  locations.forEach((l, index) => {
    if (!hasStopDetails(l.details)) return;
    const d = cleanStopDetails(l.details);
    out.push({
      index,
      contact_name: d.contact_name,
      contact_phone: d.contact_phone,
      reference: d.reference,
      instructions: d.instructions,
      notify_sms: d.notify_sms,
      lat: l.lat,
      lng: l.lng,
    });
  });
  return out;
}

export interface CreateOrderInput {
  token: string | null | undefined;
  company_id: unknown;
  user_id: unknown;
  pricing_uuid: string;
  vehicle_type_id: number;
  order_type: OrderType;
  pickup_mode: PickupMode;
  /** The picked Nairobi wall-clock string; ignored for ASAP. */
  pickup_time: string;
  goods_description: string;
  notes: string;
  manifest: string;
  locations: ReadonlyArray<DraftLocation>;
  /** Later phase: sent only when given. */
  loaders_count?: number;
  /** Sent only when given (utils/preferredDriver preferredDriverForOrder). */
  preferred_driver?: { value: string } | null;
  /** Clock, for tests. */
  now?: number;
}

/**
 * The createOrder body. Today's fields are unchanged; the new ones are all
 * optional for orders, so an orders build without them ignores them.
 */
export function buildCreateOrderBody(input: CreateOrderInput): Record<string, unknown> {
  // Dedicated is always scheduled (orders refuses it otherwise with
  // dedicated_schedule_required), whatever the mode left from On-Demand.
  const pickupMode: PickupMode = input.order_type === "dedicated" ? "scheduled" : input.pickup_mode;
  const body: Record<string, unknown> = {
    token: input.token,
    company_id: input.company_id,
    user_id: input.user_id,
    pricing_uuid: input.pricing_uuid,
    vehicle_type: input.vehicle_type_id,
    payment_plan: 1,
    payment_method: 1,
    source: "web",
    scheduled_time: resolveScheduledTime(pickupMode, input.pickup_time, input.now ?? Date.now()),
    notes: input.notes,
    manifest: input.manifest,
    order_type: input.order_type,
    pickup_mode: pickupMode,
    goods_description: cleanGoodsDescription(input.goods_description),
    stops: buildStopsPayload(input.locations),
  };
  if (typeof input.loaders_count === "number" && Number.isInteger(input.loaders_count) && input.loaders_count > 0) {
    body.loaders_count = input.loaders_count;
  }
  const preferred = input.preferred_driver?.value?.trim();
  if (preferred) body.preferred_driver = { value: preferred };
  return body;
}

/**
 * Where a refused createOrder sends the customer:
 * - "details": something on the Details step needs fixing; keep the quote;
 * - "requote": the quote no longer fits the stops; get a new price;
 * - "other": anything else (an expired quote, say): today's reset.
 */
export function createOrderErrorAction(code: unknown): "details" | "requote" | "other" {
  switch (code) {
    case "goods_description_has_phone":
    case "goods_description_invalid":
    case "contact_phone_invalid":
    case "scheduled_time_invalid":
    case "pickup_mode_invalid":
    case "preferred_driver_invalid":
    case "loaders_invalid":
    // Loaders can't be recorded right now: the customer can remove them and
    // confirm again on the same quote (orders released its lock).
    case "loaders_temporarily_unavailable":
    // Dedicated: no pickup time, a time outside 2 hours to 30 days, or the
    // company no longer has Dedicated bookings. orders' message says which.
    case "dedicated_schedule_required":
    case "dedicated_lead_time":
    case "dedicated_not_enabled":
      return "details";
    case "stops_mismatch":
    case "loaders_not_available":
      return "requote";
    default:
      return "other";
  }
}

export const STOPS_MISMATCH_MESSAGE = "Your stops changed after pricing. Get a new price.";

/**
 * What to tell the customer about createOrder's data.warnings once the order
 * exists, or null when nothing needs saying.
 * - stop_details_unavailable: orders could not store the stop contacts, so
 *   the driver has no receiver to call.
 * - order_extra_info_unavailable alone: the order went through without the
 *   goods / pickup mode columns; nothing the customer must act on.
 */
export function placedOrderNotice(warnings: unknown): string | null {
  if (!Array.isArray(warnings)) return null;
  if (warnings.includes("stop_details_unavailable")) {
    return "We couldn't save the stop contacts, so the driver will call you for directions.";
  }
  return null;
}
